import { Lock } from "lucide-react";
import {
  MARCADORES_DA_MENSAGEM,
  MENSAGEM_PADRAO_DA_ANAMNESE,
  RENOVACOES_DA_ANAMNESE,
  VALIDADES_DO_LINK_DA_ANAMNESE,
  montarMensagemDaAnamnese,
  type ConfiguracaoDePacientes,
} from "@bookdental/schemas";
import { useFeature } from "@/lib/use-feature";
import { Field, Toggle, settingsInputCls } from "@/components/settings/primitives";

type Anamnese = ConfiguracaoDePacientes["anamnese"];

/** O entorno da anamnese. As perguntas não mudam aqui: são validadas e versionadas no sistema. */
export function SecaoAnamnese({ valor, onChange }: { valor: ConfiguracaoDePacientes; onChange: (v: ConfiguracaoDePacientes) => void }) {
  const liberada = useFeature("patient.anamnese");
  const a = valor.anamnese;
  const mudar = (parcial: Partial<Anamnese>) => onChange({ ...valor, anamnese: { ...a, ...parcial } });

  if (!liberada) {
    return (
      <p className="flex items-start gap-2 rounded-lg border border-zinc-200 px-4 py-3 text-[12.5px] text-zinc-600 dark:border-border dark:text-muted-foreground">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        A anamnese ainda não está liberada para esta organização. Quando estiver, a validade do link, a mensagem e a renovação
        se configuram aqui.
      </p>
    );
  }

  const semLink = !a.mensagem.includes("{link}");
  const exemplo = montarMensagemDaAnamnese(a.mensagem, { nome: "Ana", clinica: "sua clínica", validade: "06/11/2026", link: "https://…/a/…" });

  return (
    <div className="space-y-6">
      <p className="text-[12.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
        As perguntas da anamnese são as mesmas para todas as clínicas: foram montadas para ser validadas por cirurgião-dentista e
        alimentam os alertas da ficha e da agenda. Aqui se ajusta o que vem em volta delas.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Validade do link" hint="Vale para os próximos envios; os links que já saíram mantêm a data deles.">
          <select className={settingsInputCls} value={a.validadeDoLinkDias} onChange={(e) => mudar({ validadeDoLinkDias: Number(e.target.value) as Anamnese["validadeDoLinkDias"] })}>
            {VALIDADES_DO_LINK_DA_ANAMNESE.map((d) => (
              <option key={d} value={d}>
                {d === 1 ? "1 dia" : `${d} dias`}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Pedir renovação" hint="Depois disso, a anamnese aparece como vencida na ficha.">
          <select className={settingsInputCls} value={a.renovarACadaMeses} onChange={(e) => mudar({ renovarACadaMeses: Number(e.target.value) as Anamnese["renovarACadaMeses"] })}>
            {RENOVACOES_DA_ANAMNESE.map((m) => (
              <option key={m} value={m}>
                {m === 0 ? "Nunca" : `A cada ${m} meses`}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Toggle
        label="Lembrar ao agendar"
        description="Ao marcar consulta para quem não tem anamnese (ou está vencida), oferecer o envio do link. Nunca impede o agendamento."
        checked={a.lembrarAoAgendar}
        onChange={(v) => mudar({ lembrarAoAgendar: v })}
      />

      <Field label="Mensagem do envio" hint={`Marcadores: ${MARCADORES_DA_MENSAGEM.join(" ")}. Sem dado de saúde: a mensagem sai por WhatsApp ou e-mail.`}>
        <textarea rows={4} maxLength={500} value={a.mensagem} onChange={(e) => mudar({ mensagem: e.target.value })} className={`${settingsInputCls} h-auto py-2`} />
      </Field>
      {semLink && <p className="text-xs font-medium text-red-600 dark:text-red-400">Coloque {"{link}"} na mensagem: sem ele o paciente não recebe o endereço.</p>}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 flex-1 rounded-md bg-zinc-50 px-3 py-2 text-xs text-zinc-600 dark:bg-muted/40 dark:text-muted-foreground">{exemplo}</p>
        <button
          type="button"
          disabled={a.mensagem === MENSAGEM_PADRAO_DA_ANAMNESE}
          onClick={() => mudar({ mensagem: MENSAGEM_PADRAO_DA_ANAMNESE })}
          className="text-xs font-semibold text-brand-700 hover:underline disabled:opacity-40 disabled:no-underline dark:text-primary"
        >
          Restaurar texto padrão
        </button>
      </div>
    </div>
  );
}
